// src/pages/Orders/OrderDetail.js

import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { renderToStaticMarkup } from "react-dom/server";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import "./OrderDetail.css";
import { LuPackageOpen } from "react-icons/lu";
import { CiDeliveryTruck } from "react-icons/ci";
import { IoIosHome } from "react-icons/io";
import { CgNotes } from "react-icons/cg";
import { FaInfoCircle, FaCheckCircle, FaEdit, FaSave, FaPrint, FaCity, FaTrash, FaPlus, FaPencilAlt, FaList } from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";
import Details from "./Details";
import Notes from "./Notes";
import { OrderLabelModal } from "./OrderLabelModal";
import { OrderLabel } from "./OrderLabel";
import { ImplantOrderLabelModal } from "./ImplantOrderLabelModal";
import axios from "axios";
import Swal from 'sweetalert2';
import { useCurrency } from '../../context/CurrencyContext';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

const api = axios.create({ baseURL: API_BASE_URL });

const getAuthToken = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || localStorage.getItem('accessToken');
  if (token) return token;
  const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];
  for (let key of possibleKeys) {
    const storedData = localStorage.getItem(key);
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.access || parsed.token || parsed.access_token) {
          return parsed.access || parsed.token || parsed.access_token;
        }
      } catch (e) { return storedData; }
    }
  }
  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// =====================================================================
// ✅ CACHE (pehle dekha hua order foran dikhta hai, peeche se taza data aa kar update karta hai)
// =====================================================================
const CACHE_PREFIX = 'orderDetailCache:';
const memCache = new Map();            // trackingId -> { order, products, notes }
const productDetailCache = new Map();  // product_Id -> poora product (category wagera ke saath)
const lookupCache = new Map();         // shopId -> { customers, addresses, allProducts, at }

const readCache = (trackingId) => {
  if (!trackingId) return null;
  if (memCache.has(trackingId)) return memCache.get(trackingId);
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + trackingId);
    if (raw) {
      const parsed = JSON.parse(raw);
      memCache.set(trackingId, parsed);
      return parsed;
    }
  } catch (e) { /* ignore */ }
  return null;
};

const writeCache = (trackingId, patch) => {
  if (!trackingId) return;
  const next = { ...(memCache.get(trackingId) || readCache(trackingId) || {}), ...patch };
  memCache.set(trackingId, next);
  try { sessionStorage.setItem(CACHE_PREFIX + trackingId, JSON.stringify(next)); } catch (e) { /* quota */ }
};

// ✅ Server ke error response se readable message nikalta hai
const extractServerErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return error?.message || fallback;

  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed.startsWith('<')) {
      const match = trimmed.match(/<pre class="exception_value">([\s\S]*?)<\/pre>/);
      if (match) {
        const text = match[1].replace(/<[^>]*>/g, '').replace(/&quot;/g, '"').replace(/&#x27;/g, "'").replace(/&amp;/g, '&').trim();
        if (text) return `Server error: ${text}`;
      }
      return fallback;
    }
    return trimmed || fallback;
  }

  if (data.error) return data.error;
  if (data.message) return data.message;
  if (data.detail) return data.detail;
  return fallback;
};

const getCategoryName = (p) => {
  const c = p?.product_category ?? p?.category;
  if (!c) return p?.category_Name || '';
  if (typeof c === 'string') return c;
  if (typeof c === 'object') return c.category_Name || c.name || '';
  return '';
};

const getCategoryObj = (p) => {
  const c = p?.product_category ?? p?.category;
  return c && typeof c === 'object' ? c : null;
};

// Sirf "dental implant" ko implant maana jata hai.
const DENTAL_IMPLANT_RE = /dental[\s_-]*implant/i;

const isImplant = (p) => {
  const name = getCategoryName(p);
  const group = getCategoryObj(p)?.group || p?.group || '';
  return DENTAL_IMPLANT_RE.test(name) || DENTAL_IMPLANT_RE.test(String(group));
};

// Print All Labels: labels ka HTML ek chhupe hue iframe me print karta hai.
const printLabelSheets = (sheetsHtml) => {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Order Labels</title>
<style>
  @page { size: 60mm 20mm; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }
  .label-sheet { width: 60mm; height: 20mm; overflow: hidden; page-break-after: always; break-after: page; }
  .label-sheet:last-child { page-break-after: auto; break-after: auto; }
</style>
</head>
<body>${sheetsHtml}</body>
</html>`);
  doc.close();

  const remove = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  setTimeout(() => {
    try {
      iframe.contentWindow.onafterprint = remove;
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      remove();
    }
    setTimeout(remove, 60000); // safety: agar afterprint na aaye
  }, 300);
};

// =====================================================================
// EDIT-MODE HELPERS (customer / address / product lookups)
// =====================================================================

const normalizeStatus = (s) => (s === 'In Process' ? 'Processing' : (s || 'Pending'));

const CURRENT_ADDR = 'current'; // order ka mojooda address (jab us ki id na aaye)

const getCustomerId = (c) => c?.id ?? c?.user_id ?? c?.customer_id ?? '';

const customerLabel = (c) => {
  const first = c.first_Name || c.firstName || '';
  const last = c.last_Name || c.lastName || '';
  const full = `${first} ${last}`.trim();
  const email = c.email || '';
  return full ? `${full}${email ? ' - ' + email : ''}` : (email || 'Unnamed customer');
};

const addrId = (a) => a?.address_Id ?? a?.id ?? a?.address_id ?? '';

// address kis customer ka hai (customer id / customer_id / customer {id})
const addrCustomerId = (a) => {
  if (!a) return null;
  if (a.customer_id !== undefined && a.customer_id !== null) return a.customer_id;
  const c = a.customer;
  if (c && typeof c === 'object') return c.id ?? c.user_id ?? null;
  return c ?? null;
};

const isValidAddress = (a) => {
  if (!a) return false;
  const area = a.area || '';
  const street = a.street_Number || a.streetNumber || '';
  const house = a.house_Number || a.houseNumber || '';
  if (area === '0' && street === '0' && house === '0') return false;
  if (area === '' && street === '' && house === '') return false;
  if (!a.city || a.city === '') return false;
  return true;
};

const addressText = (a) => {
  if (!a) return '';
  const house = a.house_Number || a.houseNumber || '';
  const street = a.street_Number || a.streetNumber || '';
  let t = a.city || '';
  if (a.area) t += ` - ${a.area}`;
  if (street) t += `, St: ${street}`;
  if (house) t += `, House: ${house}`;
  return t;
};

// ek customer ke saare valid, unique addresses
// (customer ke apne fields + /user/addresses/ ki alag list, bilkul Place Order ki tarah)
const getCustomerAddresses = (customer, allAddresses = []) => {
  if (!customer) return [];
  const out = [];
  const seen = new Set();
  const add = (a) => {
    if (!a || !isValidAddress(a)) return;
    const id = String(addrId(a));
    if (seen.has(id)) return;
    seen.add(id);
    out.push(a);
  };
  (customer.addresses || []).forEach(add);
  add(customer.address_details);
  add(customer.address);

  const cid = getCustomerId(customer);
  allAddresses.forEach((a) => {
    const owner = addrCustomerId(a);
    if (owner !== null && owner !== undefined && String(owner) === String(cid)) add(a);
  });
  return out;
};

// paginated API ho to saare pages le aao
const fetchAllPages = async (url) => {
  let next = url;
  let all = [];
  for (let page = 0; page < 30 && next; page++) {
    const res = await api.get(next);
    const data = res.data;
    if (Array.isArray(data)) return data;
    all = all.concat(data?.results || data?.products || data?.data || []);
    next = data?.next || null;
  }
  return all;
};

const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// ---------------------------------------------------------------
// PRODUCT FILTERS (edit mode: Type / Category / Variation / Company / Connection)
// ---------------------------------------------------------------
const FILTER_DEFS = {
  type:      { label: 'Type',               id: (p) => p.type?.type_id,                       name: (p) => p.type?.type_name },
  category:  { label: 'Category',           id: (p) => p.product_category?.catrgort_id,       name: (p) => p.product_category?.category_Name },
  variation: { label: 'Variation',          id: (p) => p.product_variation?.variation_id,     name: (p) => p.product_variation?.variation_name },
  company:   { label: 'Implant Company',    id: (p) => p.implant_company?.company_id,         name: (p) => p.implant_company?.company_name },
  brand:     { label: 'Implant Connection', id: (p) => p.product_brand?.brand_id,             name: (p) => p.product_brand?.brand_name }
};
const FILTER_KEYS = Object.keys(FILTER_DEFS);
const EMPTY_FILTERS = { type: '', category: '', variation: '', company: '', brand: '' };

const matchesProdFilters = (p, f, skipKey = null) =>
  FILTER_KEYS.every((k) => {
    if (k === skipKey || !f[k]) return true;
    const id = FILTER_DEFS[k].id(p);
    return id !== undefined && id !== null && String(id) === String(f[k]);
  });

// ek filter ke options: sirf wo jo baaki chune hue filters ke saath asal products me maujood hain
const optionsForFilter = (list, f, key) => {
  const map = new Map();
  list.forEach((p) => {
    if (!matchesProdFilters(p, f, key)) return;
    const id = FILTER_DEFS[key].id(p);
    if (id === undefined || id === null) return;
    if (!map.has(String(id))) map.set(String(id), FILTER_DEFS[key].name(p) || String(id));
  });
  return Array.from(map.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

// Searchable product dropdown (naam ya SKU se dhoondo).
// Dropdown page ke upar (portal) khulta hai, is liye table ke andar kat/overlap nahi hota.
const ProductPicker = ({ value, products, loading, onPick }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);

  const updateRect = () => {
    if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
  };

  useEffect(() => {
    if (!open) return undefined;
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open]);

  const q = text.trim().toLowerCase();
  const results = (q
    ? products.filter(p =>
        (p.product_Name || '').toLowerCase().includes(q) ||
        (p.SKU || '').toLowerCase().includes(q))
    : products
  ).slice(0, 300);

  let dropdownStyle = null;
  if (open && rect) {
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 240 && rect.top > spaceBelow;
    dropdownStyle = {
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 300),
      zIndex: 10000,
      background: '#fff',
      border: '1px solid #cfd8e6',
      borderRadius: 6,
      maxHeight: 260,
      overflowY: 'auto',
      boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
      textAlign: 'left',
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 2 }
        : { top: rect.bottom + 2 })
    };
  }

  return (
    <div style={{ position: 'relative', minWidth: 220 }}>
      <input
        ref={inputRef}
        type="text"
        className="edit-input-field"
        value={open ? text : value}
        placeholder="Search product by name or SKU"
        onFocus={() => { setText(''); setOpen(true); }}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && dropdownStyle && createPortal(
        <div style={dropdownStyle}>
          {loading && products.length === 0 ? (
            <div style={{ padding: '8px 12px', color: '#888', fontSize: 13 }}>Loading products...</div>
          ) : results.length === 0 ? (
            <div style={{ padding: '8px 12px', color: '#888', fontSize: 13 }}>No products found</div>
          ) : (
            results.map((p) => (
              <div
                key={p.product_Id}
                onMouseDown={(e) => { e.preventDefault(); onPick(p); setOpen(false); }}
                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f0f0f0', color: '#222' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f7ff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
              >
                {p.product_Name} {p.SKU ? `(${p.SKU})` : ''}
              </div>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

// Status stepper: Returned par aakhri step red
const STEPPER_CSS = `
.status-step.returned .status-circle,
.status-step.returned .status-circle svg {
  background-color: #dc3545 !important;
  border-color: #dc3545 !important;
  color: #fff !important;
  fill: #fff !important;
}
.status-step.returned .status-icon-top,
.status-step.returned .status-icon-top svg,
.status-step.returned .status-label {
  color: #dc3545 !important;
}
`;

const OrderDetail = () => {
  const { trackingId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  // ✅ Pehle render se hi data: list page ka state, ya pichli dafa ka cache
  const [initialCache] = useState(() => readCache(trackingId) || {});
  const stateOrder =
    location.state?.order && location.state.order.order_Tracking_Id === trackingId
      ? location.state.order
      : null;

  const [order, setOrder] = useState(stateOrder || initialCache.order || null);
  const [orderLoadDone, setOrderLoadDone] = useState(!!(stateOrder || initialCache.order));
  const [isEditing, setIsEditing] = useState(false);

  // Purani / doosri request naye data ko overwrite na kare
  const loadSeqRef = useRef(0);
  const isEditingRef = useRef(false);
  isEditingRef.current = isEditing;

  const buildFormFromOrder = (o) => ({
    order_Placment_Date: o.order_Placment_Date ? o.order_Placment_Date.split('T')[0] : '',
    order_Status: normalizeStatus(o.order_Status),
    delivery_Charges: o.delivery_Charges || 0,
    total_Amount: o.total_Amount || 0,
    note: o.note || '',
    customer_id: o.customer?.id ?? '',
    customer_first_Name: o.customer?.first_Name || '',
    customer_last_Name: o.customer?.last_Name || '',
    customer_phone: o.customer?.phone_Number || o.customer?.phone || '',
    customer_email: o.customer?.email || '',
    address_id: addrId(o.address) || (o.address ? CURRENT_ADDR : ''),
    address_city: o.address?.city || '',
    address_area: o.address?.area || '',
    address_street: o.address?.street_Number || '',
    address_house: o.address?.house_Number || ''
  });

  const [formData, setFormData] = useState({
    order_Placment_Date: '',
    order_Status: 'Pending',
    delivery_Charges: 0,
    total_Amount: 0,
    note: '',
    customer_id: '',
    customer_first_Name: '',
    customer_last_Name: '',
    customer_phone: '',
    customer_email: '',
    address_id: '',
    address_city: '',
    address_area: '',
    address_street: '',
    address_house: ''
  });

  const [activeTab, setActiveTab] = useState("details");
  const [orderNotes, setOrderNotes] = useState(initialCache.notes || []);
  const [showOrderLabel, setShowOrderLabel] = useState(false);
  const [showImplantLabel, setShowImplantLabel] = useState(false);
  const [implantLabelProduct, setImplantLabelProduct] = useState(null);
  const [products, setProducts] = useState(initialCache.products || []);
  // ✅ "Loading..." / "No products found" sirf tab jab sach me load ho raha / ho chuka ho
  const [productsLoaded, setProductsLoaded] = useState(!!initialCache.products);
  const productsLoading = !productsLoaded;

  const [showStockModal, setShowStockModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [cityStockData, setCityStockData] = useState([]);
  const [savingStock, setSavingStock] = useState(false);

  // Edit mode data
  const [customers, setCustomers] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allProductsLoading, setAllProductsLoading] = useState(false);
  const [editProducts, setEditProducts] = useState([]);
  const [addressMode, setAddressMode] = useState('select'); // 'select' | 'manual'
  const [prodFilters, setProdFilters] = useState(EMPTY_FILTERS);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;
    if (imagePath.startsWith('/')) return `${API_BASE_URL}${imagePath}`;
    return `${API_BASE_URL}/${imagePath}`;
  };

  const getProgressStep = (status) => {
    const s = status ? status.toLowerCase() : '';
    if (s === 'delivered' || s === 'returned') return 4;
    if (s === 'shipped' || s === 'on the way to deliver') return 3;
    if (s === 'in process' || s === 'under package' || s === 'processing') return 2;
    return 1;
  };

  const makeQrCode = (sku, fallbackId) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(sku || `product-${fallbackId}`)}`;

  // Ek ordered item ko table row me badalta hai
  const buildProductRow = (item) => {
    const baseProduct = item.product_Id || {};
    const fullProduct = productDetailCache.get(baseProduct.product_Id);
    const product = fullProduct ? { ...baseProduct, ...fullProduct } : baseProduct;
    const imageGallery = product.imageGallery || {};
    const rawImage = imageGallery.image1 || imageGallery.image2 || imageGallery.image3 || imageGallery.image4 || null;

    // Backend se aane wali price_per_unit use karein
    let pricePerUnit = 0;
    if (item.price_per_unit !== undefined && item.price_per_unit !== null) {
      pricePerUnit = parseFloat(item.price_per_unit);
    } else if (item.custom_price !== null && item.custom_price !== undefined) {
      pricePerUnit = parseFloat(item.custom_price);
    } else {
      pricePerUnit = parseFloat(product.product_Price || 0);
    }

    const qrPayload = product.SKU || `product-${product.product_Id || item.id}`;

    let productCityStock = [];
    if (product.city_stocks && Array.isArray(product.city_stocks)) {
      productCityStock = product.city_stocks.map(cs => ({ city: cs.city, stock: parseInt(cs.stock) || 0 }));
    } else if (product.cities_stock && Array.isArray(product.cities_stock)) {
      productCityStock = product.cities_stock.map(cs => ({ city: cs.city, stock: parseInt(cs.stock) || 0 }));
    } else if (product.stock_by_city && Array.isArray(product.stock_by_city)) {
      productCityStock = product.stock_by_city.map(cs => ({ city: cs.city, stock: parseInt(cs.stock) || 0 }));
    }

    return {
      id: item.id,
      productNo: product.product_Id || '—',
      name: product.product_Name || 'Unnamed Product',
      price: pricePerUnit,
      qty: item.quantity || 0,
      sku: product.SKU || '',
      city: item.city || '',
      image: getImageUrl(rawImage),
      qrCode: `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrPayload)}`,
      raw: product,
      cityStock: productCityStock,
      returnedQty: parseInt(item.returned_qty, 10) || 0
    };
  };

  // ✅ Background me sirf un products ki category/detail lata hai jin me wo missing hai
  // (pehle poori products list fetch hoti thi aur table us ka intezar karta tha)
  const enrichProducts = async (data, seq, cacheKey) => {
    const missing = data
      .map((it) => it.product_Id)
      .filter((p) => p && p.product_Id && !getCategoryName(p) && !productDetailCache.has(p.product_Id));
    const unique = [...new Map(missing.map((p) => [p.product_Id, p])).values()];
    if (unique.length === 0) return;

    let gotAny = false;
    const results = await Promise.allSettled(
      unique.map((p) => api.get(`/api/product/products/${p.product_Id}/`))
    );
    results.forEach((r, i) => {
      if (r.status === 'fulfilled' && r.value?.data) {
        productDetailCache.set(unique[i].product_Id, r.value.data);
        gotAny = true;
      }
    });

    // Detail endpoint na chale to purana tareeqa (list), lekin ab background me
    if (!gotAny) {
      try {
        const list = await fetchAllPages('/api/product/products/?page_size=500');
        list.forEach((p) => { productDetailCache.set(p.product_Id, p); });
        gotAny = list.length > 0;
      } catch (e) { console.error(e); }
    }

    if (!gotAny || seq !== loadSeqRef.current) return;
    const rebuilt = data.map(buildProductRow);
    setProducts(rebuilt);
    writeCache(cacheKey, { products: rebuilt });
  };

  const fetchOrderedProducts = async (orderId, seq = loadSeqRef.current) => {
    if (!orderId) return;
    try {
      const response = await api.get(`/order/orderdProducts/${orderId}`);
      if (seq !== loadSeqRef.current) return; // order badal chuka / purani request
      const data = Array.isArray(response.data) ? response.data : [];

      const transformed = data.map(buildProductRow);
      setProducts(transformed);
      setProductsLoaded(true);
      writeCache(trackingId, { products: transformed });

      // non-blocking
      enrichProducts(data, seq, trackingId);
    } catch (error) {
      console.error('Error fetching ordered products:', error);
    } finally {
      if (seq === loadSeqRef.current) setProductsLoaded(true);
    }
  };

  const fetchOrderNotes = async (orderId, seq = loadSeqRef.current) => {
    if (!orderId) return;
    try {
      const response = await api.get(`/order/api/order-notes/?order_id=${orderId}`);
      if (seq !== loadSeqRef.current) return;
      let notesData = [];
      if (Array.isArray(response.data)) notesData = response.data;
      else if (response.data && response.data.notes) notesData = response.data.notes;
      else if (response.data && response.data.results) notesData = response.data.results;

      const validNotes = notesData
        .map(n => (n.note || '').trim())
        .filter(text => text.length > 0);

      setOrderNotes(validNotes);
      writeCache(trackingId, { notes: validNotes });
    } catch (error) {
      console.error('Error fetching order notes:', error);
    }
  };

  // ---------------------------------------------------------------
  // ✅ LOAD: cache/state se foran dikhao, products + notes parallel, peeche se refresh
  // ---------------------------------------------------------------
  useEffect(() => {
    const seq = ++loadSeqRef.current;
    const isStale = () => seq !== loadSeqRef.current;

    const cached = readCache(trackingId) || {};
    const seeded =
      (location.state?.order && location.state.order.order_Tracking_Id === trackingId ? location.state.order : null) ||
      cached.order ||
      null;

    setOrder(seeded);
    setProducts(cached.products || []);
    setProductsLoaded(!!cached.products);
    setOrderNotes(cached.notes || []);
    setOrderLoadDone(!!seeded);
    if (seeded && !isEditingRef.current) setFormData(buildFormFromOrder(seeded));

    const seededOrderId = seeded?.order_Id;

    const run = async () => {
      // order pehle se maloom hai to products aur notes order ke intezar ke baghair abhi shuru
      if (seededOrderId) {
        fetchOrderedProducts(seededOrderId, seq);
        fetchOrderNotes(seededOrderId, seq);
      }

      try {
        const response = await api.get(`/order/getAllorder/`);
        if (isStale()) return;
        const orders = response.data.orders || response.data;
        const fresh = orders.find(o => o.order_Tracking_Id === trackingId);

        if (!fresh) {
          if (!seeded) { setOrderLoadDone(true); setProductsLoaded(true); }
          return;
        }

        setOrder(fresh);
        if (!isEditingRef.current) setFormData(buildFormFromOrder(fresh));
        setOrderLoadDone(true);

        if (!seededOrderId || String(fresh.order_Id) !== String(seededOrderId)) {
          fetchOrderedProducts(fresh.order_Id, seq);
          fetchOrderNotes(fresh.order_Id, seq);
        }
      } catch (error) {
        console.error('Error loading order:', error);
        if (!isStale()) {
          setOrderLoadDone(true);
          if (!seededOrderId) setProductsLoaded(true);
        }
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingId]);

  // Order badalte hi cache me (sirf isi tracking id ka order)
  useEffect(() => {
    if (order && order.order_Tracking_Id === trackingId) {
      writeCache(trackingId, { order });
    }
  }, [order, trackingId]);

  // ---------------------------------------------------------------
  // TOTAL: edit mode me products / price / qty / delivery badalte hi total dobara banta hai
  // ---------------------------------------------------------------
  const calcTotal = (subtotal, delivery) => {
    if (!order) return subtotal + delivery;
    const origSubtotal = products.reduce((s, p) => s + toNum(p.price) * (parseInt(p.qty, 10) || 0), 0);
    const origDelivery = toNum(order.delivery_Charges);
    const hasBreakdown = order.vat_amount !== undefined || order.discount !== undefined;

    let total;
    if (hasBreakdown) {
      const vat = toNum(order.vat_amount);
      const vatRate = origSubtotal > 0 && vat > 0 ? vat / origSubtotal : (order.apply_vat ? 0.20 : 0);
      total = subtotal + subtotal * vatRate + delivery - toNum(order.discount);
    } else {
      const residual = toNum(order.total_Amount) - origSubtotal - origDelivery;
      total = subtotal + delivery + residual;
    }
    return Math.max(0, Math.round(total * 100) / 100);
  };

  useEffect(() => {
    if (!isEditing || !order) return;
    const subtotal = editProducts.reduce(
      (s, p) => s + toNum(p.price) * (parseInt(p.qty, 10) || 0), 0
    );
    const total = calcTotal(subtotal, toNum(formData.delivery_Charges));
    setFormData(prev => ({ ...prev, total_Amount: total.toFixed(2) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, editProducts, formData.delivery_Charges]);

  // ---------------------------------------------------------------
  // EDIT MODE
  // ---------------------------------------------------------------
  const getOrderShopId = () =>
    order?.shop_id || order?.shop?.shop_id || (typeof order?.shop === 'number' ? order.shop : null) ||
    localStorage.getItem('selectedShopId');

  const toEditRow = (p) => ({
    key: `row-${p.id}`,
    id: p.id,
    product_Id: p.productNo,
    name: p.name,
    sku: p.sku,
    price: p.price,
    qty: p.qty,
    city: p.city || '',
    image: p.image,
    qrCode: p.qrCode
  });

  // ✅ Teeno lookups parallel, aur cache se foran (pehle ek ke baad ek aate the)
  const loadEditLookups = async () => {
    const shopId = getOrderShopId();
    const q = shopId ? `shop_id=${shopId}` : '';
    const key = String(shopId || 'all');
    const cached = lookupCache.get(key);

    if (cached) {
      setCustomers(cached.customers);
      setAddresses(cached.addresses);
      setAllProducts(cached.allProducts);
      if (Date.now() - cached.at < 5 * 60 * 1000) return; // abhi taaza hai
    } else {
      setAllProductsLoading(true);
    }

    try {
      const [cRes, aRes, pRes] = await Promise.allSettled([
        api.get(`/user/getAllcustomers/${q ? `?${q}` : ''}`),
        api.get(`/user/addresses/${q ? `?${q}` : ''}`),
        fetchAllPages(`/api/product/products/?${q}${q ? '&' : ''}page_size=500`)
      ]);

      const next = {
        customers: cached?.customers || [],
        addresses: cached?.addresses || [],
        allProducts: cached?.allProducts || [],
        at: Date.now()
      };

      if (cRes.status === 'fulfilled') {
        const d = cRes.value.data;
        next.customers = Array.isArray(d) ? d : (d.results || d.customers || d.data || []);
        setCustomers(next.customers);
      } else {
        console.error('Error fetching customers:', cRes.reason);
      }

      if (aRes.status === 'fulfilled') {
        const d = aRes.value.data;
        next.addresses = Array.isArray(d) ? d : (d.addresses || d.results || d.data || []);
        setAddresses(next.addresses);
      } else {
        console.error('Error fetching addresses:', aRes.reason);
      }

      if (pRes.status === 'fulfilled') {
        next.allProducts = pRes.value;
        setAllProducts(next.allProducts);
      } else {
        console.error('Error fetching products:', pRes.reason);
      }

      lookupCache.set(key, next);
    } finally {
      setAllProductsLoading(false);
    }
  };

  // Order khulne ke thori der baad background me lookups la lo, taake "Edit Details" dabate hi tayyar hon
  useEffect(() => {
    if (!order?.order_Id) return undefined;
    const t = setTimeout(() => { loadEditLookups(); }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.order_Id]);

  const handleEdit = () => {
    if (order) {
      setFormData(buildFormFromOrder(order));
      setAddressMode(order.address ? 'select' : 'manual');
    }
    setEditProducts(products.map(toEditRow));
    setProdFilters(EMPTY_FILTERS);
    setIsEditing(true);
    loadEditLookups();
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (order) setFormData(buildFormFromOrder(order));
    setEditProducts([]);
    setProdFilters(EMPTY_FILTERS);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ---- customer dropdown: select karte hi phone / email / address auto-fill ----
  const customerOptions = (() => {
    const list = [...customers];
    const cur = order?.customer;
    if (cur && cur.id !== undefined && !list.some(c => String(getCustomerId(c)) === String(cur.id))) {
      list.unshift(cur);
    }
    return list;
  })();

  const handleCustomerSelect = (customerId) => {
    const c = customerOptions.find(x => String(getCustomerId(x)) === String(customerId));
    if (!c) {
      setFormData(prev => ({
        ...prev, customer_id: customerId, customer_first_Name: '', customer_last_Name: '',
        customer_phone: '', customer_email: '', address_id: ''
      }));
      return;
    }

    const sameAsOrderCustomer = String(customerId) === String(order?.customer?.id);
    const addrs = getCustomerAddresses(c, addresses);

    let nextAddressId = '';
    if (sameAsOrderCustomer && order?.address) {
      nextAddressId = addrId(order.address) || CURRENT_ADDR;   // wapas purana address
    } else if (addrs.length > 0) {
      nextAddressId = addrId(addrs[0]);                        // naye customer ka pehla valid address
    }

    setAddressMode('select');
    setFormData(prev => ({
      ...prev,
      customer_id: getCustomerId(c),
      customer_first_Name: c.first_Name || c.firstName || '',
      customer_last_Name: c.last_Name || c.lastName || '',
      customer_phone: c.phone_Number || c.phone || '',
      customer_email: c.email || '',
      address_id: nextAddressId
    }));
  };

  const getAddressOptions = () => {
    const c = customerOptions.find(x => String(getCustomerId(x)) === String(formData.customer_id));
    let list = c ? getCustomerAddresses(c, addresses) : [];

    // order ka mojooda address: sirf tab jab customer wahi ho
    const cur = order?.address;
    if (cur && String(formData.customer_id) === String(order?.customer?.id)) {
      const curId = addrId(cur);
      if (curId !== '' && curId !== null && curId !== undefined) {
        if (!list.some(a => String(addrId(a)) === String(curId))) list = [cur, ...list];
      } else {
        list = [{ ...cur, address_Id: CURRENT_ADDR }, ...list];
      }
    }
    return list;
  };

  const switchAddressToManual = () => {
    const sel = getAddressOptions().find(a => String(addrId(a)) === String(formData.address_id));
    setFormData(prev => ({
      ...prev,
      address_city: sel?.city || prev.address_city,
      address_area: sel?.area || prev.address_area,
      address_street: sel?.street_Number || prev.address_street,
      address_house: sel?.house_Number || prev.address_house
    }));
    setAddressMode('manual');
  };

  // ---- product filters (edit mode) ----
  const handleProdFilterChange = (key, value) => {
    setProdFilters((prev) => {
      const next = { ...prev, [key]: value };
      FILTER_KEYS.forEach((k) => {
        if (k === key || !next[k]) return;
        const stillValid = optionsForFilter(allProducts, next, k).some((o) => o.id === String(next[k]));
        if (!stillValid) next[k] = '';
      });
      return next;
    });
  };

  const hasProdFilter = FILTER_KEYS.some((k) => prodFilters[k]);
  const filteredPickerProducts = allProducts.filter((p) => matchesProdFilters(p, prodFilters));

  // ---- products (edit mode) ----
  const handlePickProduct = (rowKey, p) => {
    const gallery = p.imageGallery || {};
    const rawImage = gallery.image1 || gallery.image2 || gallery.image3 || gallery.image4 || null;
    setEditProducts(prev => prev.map(r => (
      r.key === rowKey
        ? {
            ...r,
            product_Id: p.product_Id,
            name: p.product_Name || 'Unnamed Product',
            sku: p.SKU || '',
            price: toNum(p.product_Price),
            image: getImageUrl(rawImage),
            qrCode: makeQrCode(p.SKU, p.product_Id)
          }
        : r
    )));
  };

  const updateEditRow = (rowKey, field, value) => {
    setEditProducts(prev => prev.map(r => (r.key === rowKey ? { ...r, [field]: value } : r)));
  };

  const addEditRow = () => {
    setEditProducts(prev => [...prev, {
      key: `new-${Date.now()}-${prev.length}`,
      id: null, product_Id: null, name: '', sku: '', price: 0, qty: 1, city: '', image: null, qrCode: null
    }]);
  };

  const removeEditRow = (rowKey) => {
    setEditProducts(prev => prev.filter(r => r.key !== rowKey));
  };

  const normalizeRows = (rows) => rows.map(r => ({
    id: r.id ?? null,
    product_Id: r.product_Id,
    quantity: parseInt(r.qty, 10) || 0,
    price_per_unit: toNum(r.price)
  }));

  const handleSave = async () => {
    // ---- validation ----
    if (editProducts.length === 0) {
      Swal.fire({ icon: 'warning', title: 'No products', text: 'An order needs at least one product.', confirmButtonColor: '#004aad' });
      return;
    }
    if (editProducts.some(r => !r.product_Id)) {
      Swal.fire({ icon: 'warning', title: 'Select a product', text: 'Please select a product in every row (or remove the empty row).', confirmButtonColor: '#004aad' });
      return;
    }
    if (editProducts.some(r => (parseInt(r.qty, 10) || 0) < 1)) {
      Swal.fire({ icon: 'warning', title: 'Invalid quantity', text: 'Quantity must be at least 1 for every product.', confirmButtonColor: '#004aad' });
      return;
    }
    if (editProducts.some(r => toNum(r.price) < 0)) {
      Swal.fire({ icon: 'warning', title: 'Invalid price', text: 'Price cannot be negative.', confirmButtonColor: '#004aad' });
      return;
    }

    const result = await Swal.fire({
      title: 'Save Changes?',
      text: "Are you sure you want to update this order?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#004aad',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        Swal.showLoading();

        const customerChanged = String(formData.customer_id) !== String(order.customer?.id ?? '');
        const productsChanged =
          JSON.stringify(normalizeRows(editProducts)) !== JSON.stringify(normalizeRows(products.map(toEditRow)));

        const payload = {
          order_Placment_Date: formData.order_Placment_Date,
          order_Status: formData.order_Status,
          delivery_Charges: parseFloat(formData.delivery_Charges) || 0,
          total_Amount: parseFloat(formData.total_Amount) || 0,
          note: formData.note
        };

        // Email / phone sirf tab bhejo jab user ne unhe khud badla ho.
        const selectedCustomer = customerOptions.find(
          (x) => String(getCustomerId(x)) === String(formData.customer_id)
        );
        const baseCustomer = customerChanged ? (selectedCustomer || {}) : (order.customer || {});
        const baseEmail = baseCustomer.email || '';
        const basePhone = baseCustomer.phone_Number || baseCustomer.phone || '';

        if (formData.customer_email !== baseEmail) payload.customer_email = formData.customer_email;
        if (formData.customer_phone !== basePhone) payload.customer_phone = formData.customer_phone;

        if (customerChanged) {
          payload.customer = Number(formData.customer_id);
        } else {
          payload.customer_first_Name = formData.customer_first_Name;
          payload.customer_last_Name = formData.customer_last_Name;
        }

        if (addressMode === 'select') {
          // 'current' = order ka purana address hi rahe, kuch bhejne ki zaroorat nahi
          if (
            formData.address_id !== '' &&
            formData.address_id !== undefined &&
            formData.address_id !== CURRENT_ADDR
          ) {
            payload.address = Number(formData.address_id);
          }
        } else {
          payload.address_city = formData.address_city;
          payload.address_area = formData.address_area;
          payload.address_street = formData.address_street;
          payload.address_house = formData.address_house;
        }

        if (productsChanged) {
          payload.products = editProducts.map(r => ({
            id: r.id ?? null,
            product_Id: r.product_Id,
            quantity: parseInt(r.qty, 10) || 0,
            price_per_unit: toNum(r.price),
            city: r.city || ''
          }));
        }

        const response = await api.put(`/order/update-order-details/${order.order_Id}/`, payload);

        setOrder(response.data);
        setIsEditing(false);
        setEditProducts([]);
        setProdFilters(EMPTY_FILTERS);
        window.dispatchEvent(new Event('orderUpdated'));
        await fetchOrderedProducts(order.order_Id);

        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Order details updated successfully!',
          confirmButtonColor: '#004aad',
          timer: 2000,
          showConfirmButton: false
        });

      } catch (error) {
        console.error('Error updating details:', error);
        Swal.fire({
          icon: 'error',
          title: 'Failed!',
          text: extractServerErrorMessage(error, 'Failed to update order details.'),
          confirmButtonColor: '#004aad'
        });
      }
    }
  };

  // Details tab me file save hone par order ka status badal sakta hai:
  const handleOrderUpdatedFromFiles = (updatedOrder) => {
    if (!updatedOrder) return;
    setOrder(updatedOrder);
    setFormData(prev => ({ ...prev, order_Status: normalizeStatus(updatedOrder.order_Status) || prev.order_Status }));
  };

  // Label: sirf dental implant ke liye implant label, baqi sab ke liye normal (60mm x 20mm)
  const handleOpenLabel = (product) => {
    if (isImplant(product?.raw)) {
      setImplantLabelProduct(product.raw);
      setShowImplantLabel(true);
    } else {
      setShowOrderLabel(true);
    }
  };

  // PRINT ALL LABELS
  const handlePrintAllLabels = async () => {
    if (productsLoading) return;

    if (products.length === 0) {
      Swal.fire({ icon: 'info', title: 'No products', text: 'This order has no products to print labels for.', confirmButtonColor: '#004aad' });
      return;
    }

    const printable = products.filter(p => !isImplant(p.raw));
    const skipped = products.filter(p => isImplant(p.raw));
    const total = printable.reduce((sum, p) => sum + (parseInt(p.qty, 10) || 0), 0);

    if (total === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Nothing to print',
        text: skipped.length > 0
          ? 'All products in this order are dental implants. Their labels have a different size, so print them one by one with "Open Label".'
          : 'No quantity to print labels for.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    if (skipped.length > 0 || total > 100) {
      const lines = [];
      lines.push(`${total} label(s) will be printed.`);
      if (skipped.length > 0) {
        lines.push(`${skipped.length} dental implant product(s) will be skipped (different label size). Print them with "Open Label".`);
      }
      const confirm = await Swal.fire({
        icon: 'question',
        title: 'Print all labels?',
        text: lines.join(' '),
        showCancelButton: true,
        confirmButtonColor: '#004aad',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Print',
        cancelButtonText: 'Cancel'
      });
      if (!confirm.isConfirmed) return;
    }

    const labelMarkup = renderToStaticMarkup(<OrderLabel order={order} />);
    const sheets = Array.from({ length: total })
      .map(() => `<div class="label-sheet">${labelMarkup}</div>`)
      .join('');

    printLabelSheets(sheets);
  };

  const handleDownloadInvoice = () => {
    navigate(`/invoice-detail/${order.order_Tracking_Id || trackingId}`, { state: { order, products } });
  };

  const handleOpenStockModal = (product) => {
    setEditingProduct(product);
    if (product.cityStock && product.cityStock.length > 0) {
      setCityStockData([...product.cityStock]);
    } else {
      setCityStockData([{ city: '', stock: 0 }]);
    }
    setShowStockModal(true);
  };

  const handleStockChange = (index, field, value) => {
    const updated = [...cityStockData];
    updated[index] = { ...updated[index], [field]: value };
    setCityStockData(updated);
  };

  const handleAddCityRow = () => {
    setCityStockData([...cityStockData, { city: '', stock: 0 }]);
  };

  const handleRemoveCityRow = (index) => {
    const updated = cityStockData.filter((_, i) => i !== index);
    setCityStockData(updated);
  };

  const handleSaveStock = async () => {
    setSavingStock(true);
    try {
      const payload = {
        city_stocks: cityStockData.filter(c => c.city.trim() !== '')
      };

      await api.patch(`/api/product/products/${editingProduct.productNo}/`, payload);

      // is product ka purana cached detail hata do taake taaza stock aaye
      productDetailCache.delete(editingProduct.productNo);

      Swal.fire({
        icon: 'success',
        title: 'Stock Updated!',
        text: 'City & Stock details saved successfully.',
        confirmButtonColor: '#004aad',
        timer: 2000,
        showConfirmButton: false
      });

      setShowStockModal(false);
      fetchOrderedProducts(order.order_Id);
    } catch (error) {
      console.error('Error saving stock:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed!',
        text: extractServerErrorMessage(error, 'Failed to update stock details.'),
        confirmButtonColor: '#004aad'
      });
    } finally {
      setSavingStock(false);
    }
  };

  if (!order) {
    return (
      <div className="no-data" style={{ padding: '50px', textAlign: 'center' }}>
        {orderLoadDone ? 'Order not found.' : 'Loading...'}
      </div>
    );
  }

  const currentStep = getProgressStep(order.order_Status);
  const isReturned = (order.order_Status || '').toLowerCase() === 'returned';

  // Print All Labels button par dikhane ke liye total labels (dental implant ke bina)
  const totalPrintableLabels = products
    .filter(p => !isImplant(p.raw))
    .reduce((sum, p) => sum + (parseInt(p.qty, 10) || 0), 0);

  // Sirf main order note (Place Order wala)
  const getDisplayNotes = () => {
    if (order.note && order.note.trim()) {
      return order.note.trim();
    }
    return 'N/A';
  };

  const addressOptions = isEditing ? getAddressOptions() : [];

  const filterSelectStyle = {
    width: '100%',
    padding: '7px 8px',
    border: '1px solid #cfd8e6',
    borderRadius: 6,
    fontSize: 13,
    background: '#fff',
    boxSizing: 'border-box'
  };

  return (
    <div className="order-detail-page">
      <style>{STEPPER_CSS}</style>

      <div className="order-top-header">
        <h1 className="order-main-title">
          Order #{order.order_Tracking_Id || trackingId}
        </h1>

        <div className="header-actions">
          <button className="download-invoice-btn" onClick={handleDownloadInvoice}>
            <FaPrint /> Download Invoice
          </button>
          <button className="back-button-top" onClick={() => navigate(-1)}>
            Back to Orders
          </button>
        </div>
      </div>

      <div className="order-status-card-wrapper">
        <div className="order-status-card">
          <h2 className="status-card-title">Order Status</h2>

          <div className="status-progress-container">
            <div className={`status-step ${currentStep >= 1 ? 'completed' : ''}`}>
              <div className="status-icon-top"><CgNotes /></div>
              <div className="status-circle"><FaCheckCircle /></div>
              <p className="status-label">Order Placed</p>
            </div>
            <div className="status-arrow"><FiChevronRight /></div>

            <div className={`status-step ${currentStep >= 2 ? 'completed' : ''}`}>
              <div className="status-icon-top"><LuPackageOpen /></div>
              <div className="status-circle"><FaCheckCircle /></div>
              <p className="status-label">Under Package</p>
            </div>
            <div className="status-arrow"><FiChevronRight /></div>

            <div className={`status-step ${currentStep >= 3 ? 'completed' : ''}`}>
              <div className="status-icon-top"><CiDeliveryTruck /></div>
              <div className="status-circle"><FaCheckCircle /></div>
              <p className="status-label">On The way</p>
            </div>
            <div className="status-arrow"><FiChevronRight /></div>

            <div className={`status-step ${currentStep >= 4 ? 'completed' : ''} ${isReturned ? 'returned' : ''}`}>
              <div className="status-icon-top"><IoIosHome /></div>
              <div className="status-circle"><FaCheckCircle /></div>
              <p className="status-label">{isReturned ? 'Returned' : 'Delivered / Return'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-card">
          <h3>Order Information</h3>

          <div className="info-row">
            <span>Tracking ID</span>
            <strong>{order.order_Tracking_Id || trackingId}</strong>
          </div>

          <div className="info-row">
            <span>Order Date</span>
            {isEditing ? (
              <input type="date" name="order_Placment_Date" value={formData.order_Placment_Date} onChange={handleInputChange} className="edit-input-field" />
            ) : (
              <strong>{order.order_Placment_Date || order.date}</strong>
            )}
          </div>

          {/* Order Time Field (Local Time Converted) */}
          <div className="info-row">
            <span>Order Time</span>
            <strong>
              {order.order_Placment_Time
                ? (() => {
                    try {
                      const today = new Date().toISOString().split('T')[0];
                      const utcDateString = `${today}T${order.order_Placment_Time}Z`;
                      const localDate = new Date(utcDateString);

                      if (isNaN(localDate.getTime())) return order.order_Placment_Time;

                      return localDate.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      });
                    } catch (e) {
                      return order.order_Placment_Time;
                    }
                  })()
                : 'N/A'}
            </strong>
          </div>

          <div className="info-row">
            <span>Status</span>
            {isEditing ? (
              <select name="order_Status" value={formData.order_Status} onChange={handleInputChange} className="edit-input-field">
                <option value="Pending">Pending</option>
                <option value="Processing">Processing</option>
                <option value="Shipped">Shipped</option>
                <option value="Delivered">Delivered</option>
                <option value="Canceled">Canceled</option>
                <option value="Returned">Returned</option>
              </select>
            ) : (
              <strong>{normalizeStatus(order.order_Status || order.status)}</strong>
            )}
          </div>

          <div className="info-row">
            <span>Delivery Charges</span>
            {isEditing ? (
              <input type="number" name="delivery_Charges" value={formData.delivery_Charges} onChange={handleInputChange} className="edit-input-field" step="0.01" />
            ) : (
              <strong>{formatPrice(order.delivery_Charges || 0)}</strong>
            )}
          </div>

          <div className="info-row total">
            <span>Total Amount</span>
            {isEditing ? (
              <input type="number" name="total_Amount" value={formData.total_Amount} onChange={handleInputChange} className="edit-input-field" step="0.01" />
            ) : (
              <strong>{formatPrice(order.total_Amount || 0)}</strong>
            )}
          </div>

          <div className="info-row">
            <span>Order Notes</span>
            {isEditing ? (
              <textarea name="note" value={formData.note} onChange={handleInputChange} className="edit-input-field" rows="2" />
            ) : (
              <strong style={{ maxWidth: '60%', wordBreak: 'break-word', textAlign: 'right' }}>
                {getDisplayNotes()}
              </strong>
            )}
          </div>
        </div>

        <div className="detail-card">
          <h3>Customer Details</h3>

          <div className="info-row">
            <span>Customer Name</span>
            {isEditing ? (
              <select
                value={formData.customer_id}
                onChange={(e) => handleCustomerSelect(e.target.value)}
                className="edit-input-field"
                style={{ width: '65%' }}
              >
                <option value="">Select a customer</option>
                {customerOptions.map((c) => (
                  <option key={getCustomerId(c)} value={getCustomerId(c)}>{customerLabel(c)}</option>
                ))}
              </select>
            ) : (
              <strong>
                {order.customer ? `${order.customer.first_Name || ''} ${order.customer.last_Name || ''}`.trim() || order.customer.email || 'N/A' : 'N/A'}
              </strong>
            )}
          </div>

          <div className="info-row">
            <span>Phone Number</span>
            {isEditing ? (
              <input type="text" name="customer_phone" value={formData.customer_phone} onChange={handleInputChange} className="edit-input-field" />
            ) : (
              <strong>{order.customer ? order.customer.phone_Number || order.customer.phone || 'N/A' : 'N/A'}</strong>
            )}
          </div>

          <div className="info-row">
            <span>Email</span>
            {isEditing ? (
              <input type="email" name="customer_email" value={formData.customer_email} onChange={handleInputChange} className="edit-input-field" />
            ) : (
              <strong>{order.customer ? order.customer.email || 'N/A' : 'N/A'}</strong>
            )}
          </div>

          <div className="info-row">
            <span>Delivery Address</span>
            {isEditing ? (
              addressMode === 'select' ? (
                <div style={{ display: 'flex', gap: '6px', width: '65%', alignItems: 'center' }}>
                  <select
                    name="address_id"
                    value={formData.address_id}
                    onChange={handleInputChange}
                    className="edit-input-field"
                    style={{ flex: 1 }}
                  >
                    <option value="">
                      {!formData.customer_id
                        ? 'Select a customer first'
                        : addressOptions.length === 0
                          ? 'No address found - use the pencil to type one'
                          : 'Select an address'}
                    </option>
                    {addressOptions.map((a) => (
                      <option key={addrId(a)} value={addrId(a)}>
                        {addressText(a)}{String(addrId(a)) === String(order?.address ? (addrId(order.address) || CURRENT_ADDR) : '') && String(formData.customer_id) === String(order?.customer?.id) ? ' (current)' : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={switchAddressToManual}
                    title="Type address manually"
                    style={{ padding: '6px 10px', border: '1px solid #004aad', background: '#f0f7ff', color: '#004aad', borderRadius: '6px', cursor: 'pointer' }}
                  >
                    <FaPencilAlt />
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '65%' }}>
                  <input type="text" name="address_city" value={formData.address_city} onChange={handleInputChange} className="edit-input-field" placeholder="City" />
                  <input type="text" name="address_area" value={formData.address_area} onChange={handleInputChange} className="edit-input-field" placeholder="Area" />
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <input type="text" name="address_street" value={formData.address_street} onChange={handleInputChange} className="edit-input-field" placeholder="Street" />
                    <input type="text" name="address_house" value={formData.address_house} onChange={handleInputChange} className="edit-input-field" placeholder="House" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setAddressMode('select')}
                    style={{ alignSelf: 'flex-start', padding: '4px 10px', border: '1px solid #004aad', background: '#f0f7ff', color: '#004aad', borderRadius: '6px', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }}
                  >
                    <FaList /> Select from list
                  </button>
                </div>
              )
            ) : (
              <strong>
                {order.address ? `${order.address.city || ''} - ${order.address.area || ''} ${order.address.street_Number ? ', St: ' + order.address.street_Number : ''} ${order.address.house_Number ? ', House: ' + order.address.house_Number : ''}`.replace(/, ,/g, ',').replace(/^,/, '') : 'N/A'}
              </strong>
            )}
          </div>

          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
            {!isEditing ? (
              <button
                onClick={handleEdit}
                style={{ padding: '8px 20px', background: '#004aad', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                <FaEdit /> Edit Details
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  style={{ padding: '8px 16px', border: '1px solid #ccc', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', color: '#555' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  style={{ padding: '8px 20px', background: '#004aad', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                >
                  <FaSave /> Save Changes
                </button>
              </>
            )}
          </div>

        </div>
      </div>

      <div className="products-section">
        <h3 className="products-title">Ordered Products</h3>

        {/* Edit mode: product filters (heading ke neeche) */}
        {isEditing && (
          <div
            style={{
              margin: '8px 0 12px',
              padding: '12px 14px',
              border: '1px solid #d6e2f5',
              background: '#f8fbff',
              borderRadius: 8
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, color: '#004aad', fontSize: 13 }}>
                Filter products
                <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
                  {allProductsLoading && allProducts.length === 0 ? 'Loading products...' : `(${filteredPickerProducts.length} of ${allProducts.length} products)`}
                </span>
              </span>
              {hasProdFilter && (
                <button
                  type="button"
                  onClick={() => setProdFilters(EMPTY_FILTERS)}
                  style={{ padding: '4px 12px', border: '1px solid #ffcccc', background: '#fff5f5', color: '#dc3545', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                >
                  Clear filters
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
              {FILTER_KEYS.map((key) => {
                const opts = optionsForFilter(allProducts, prodFilters, key);
                return (
                  <div key={key}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>
                      {FILTER_DEFS[key].label} <span style={{ fontWeight: 400 }}>({opts.length})</span>
                    </label>
                    <select
                      value={prodFilters[key]}
                      onChange={(e) => handleProdFilterChange(key, e.target.value)}
                      style={filterSelectStyle}
                    >
                      <option value="">All</option>
                      {opts.map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Print All Labels / Add Product */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', margin: '4px 0 12px', flexWrap: 'wrap' }}>
          {isEditing && (
            <button
              onClick={addEditRow}
              style={{ padding: '8px 18px', background: '#e6f0ff', border: '1px dashed #004aad', color: '#004aad', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <FaPlus /> Add Product
            </button>
          )}
          <button
            onClick={handlePrintAllLabels}
            disabled={productsLoading || products.length === 0 || isEditing}
            title="Print labels for every product (one label per quantity)"
            style={{
              padding: '8px 18px',
              background: '#004aad',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: productsLoading || products.length === 0 || isEditing ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              opacity: productsLoading || products.length === 0 || isEditing ? 0.6 : 1
            }}
          >
            <FaPrint /> Print All Labels{totalPrintableLabels > 0 ? ` (${totalPrintableLabels})` : ''}
          </button>
        </div>

        <div className="products-table-container">
          <table className="products-table">
            <thead>
              <tr>
                <th>IMAGE</th>
                <th>PRODUCT NO.</th>
                <th>NAME</th>
                <th>PRICE</th>
                <th>QTY</th>
                <th>SKU</th>
                <th>CITIES & STOCK</th>
                <th>QR CODE</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {isEditing ? (
                editProducts.length > 0 ? (
                  editProducts.map((row) => (
                    <tr key={row.key}>
                      <td className="product-image">
                        {row.image ? (
                          <img src={row.image} alt={row.name} className="product-thumb" onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : <span className="image-placeholder-text">No Image</span>}
                      </td>
                      <td>{row.product_Id || '—'}</td>
                      <td className="product-name">
                        <ProductPicker
                          value={row.name}
                          products={filteredPickerProducts}
                          loading={allProductsLoading}
                          onPick={(p) => handlePickProduct(row.key, p)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="edit-input-field"
                          style={{ width: 100 }}
                          value={row.price}
                          onChange={(e) => updateEditRow(row.key, 'price', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          className="edit-input-field"
                          style={{ width: 70 }}
                          value={row.qty}
                          onChange={(e) => updateEditRow(row.key, 'qty', e.target.value)}
                        />
                      </td>
                      <td>{row.sku || '—'}</td>
                      <td style={{ textAlign: 'center', color: '#999' }}>—</td>
                      <td className="qr-code-cell">
                        {row.qrCode ? <img src={row.qrCode} alt="QR" className="qr-code-image" /> : '—'}
                      </td>
                      <td className="action-cell">
                        <button
                          onClick={() => removeEditRow(row.key)}
                          title="Remove product"
                          style={{ background: '#ffe6e6', color: '#dc3545', border: '1px solid #ffcccc', borderRadius: '4px', padding: '6px 12px', cursor: 'pointer' }}
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="9" style={{ textAlign: 'center', padding: '30px' }}>No products. Click "Add Product".</td></tr>
                )
              ) : productsLoading ? (
                <tr><td colSpan="9" style={{ textAlign: 'center', padding: '30px' }}>Loading...</td></tr>
              ) : products.length > 0 ? (
                products.map((product) => (
                  <tr key={product.id} style={product.returnedQty > 0 ? { background: '#fff1f1' } : undefined}>
                    <td className="product-image" style={{ position: 'relative' }}>
                      {product.image ? (
                        <img src={product.image} alt={product.name} className="product-thumb" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : <span className="image-placeholder-text">No Image</span>}
                      {product.returnedQty > 0 && (
                        <span style={{ position: 'absolute', top: 4, left: 4, background: '#dc3545', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>
                          ↩ RETURN
                        </span>
                      )}
                    </td>
                    <td>{product.productNo}</td>
                    <td className="product-name">
                      {product.name}
                      {product.returnedQty > 0 && (
                        <div style={{ color: '#dc3545', fontSize: 12, fontWeight: 700, marginTop: 2 }}>
                          ⚠ Returned: {product.returnedQty} of {product.qty}
                        </div>
                      )}
                    </td>
                    <td>{formatPrice(product.price)}</td>
                    <td>{product.qty}</td>
                    <td>{product.sku || '—'}</td>

                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => handleOpenStockModal(product)}
                        style={{
                          background: '#f0f7ff', border: '1px solid #004aad', color: '#004aad',
                          padding: '6px 12px', borderRadius: '4px', cursor: 'pointer',
                          fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '5px'
                        }}
                      >
                        <FaCity /> View / Edit
                      </button>
                    </td>

                    <td className="qr-code-cell"><img src={product.qrCode} alt="QR" className="qr-code-image" /></td>
                    <td className="action-cell">
                      <button className="open-label-btn" onClick={() => handleOpenLabel(product)}>Open Label</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="9" style={{ textAlign: 'center', padding: '30px' }}>No products found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="tabs-section">
        <div className="tabs-header">
          <button className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>
            <FaInfoCircle className="tab-icon" /> Details
          </button>
          <button className={`tab-btn ${activeTab === 'notes' ? 'active' : ''}`} onClick={() => setActiveTab('notes')}>
            <CgNotes className="tab-icon" /> Notes
          </button>
        </div>
        <div className="tabs-content">
          {activeTab === 'details' && (
            <div className="details-tab">
              <Details
                orderId={order.order_Id}
                order={order}
                products={products}
                onOrderUpdated={handleOrderUpdatedFromFiles}
                onProductsChanged={() => fetchOrderedProducts(order.order_Id)}
              />
            </div>
          )}
          {activeTab === 'notes' && <div className="notes-tab"><Notes orderId={order.order_Id} order={order} /></div>}
        </div>
      </div>

      {showOrderLabel && <OrderLabelModal order={order} onClose={() => setShowOrderLabel(false)} />}
      {showImplantLabel && implantLabelProduct && (
        <ImplantOrderLabelModal product={implantLabelProduct} onClose={() => { setShowImplantLabel(false); setImplantLabelProduct(null); }} />
      )}

      {showStockModal && editingProduct && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', borderRadius: '8px', width: '500px', maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderBottom: '1px solid #eee' }}>
              <h3 style={{ margin: 0, fontSize: '18px', color: '#004aad' }}>Cities & Stock</h3>
              <button onClick={() => setShowStockModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#999' }}>&times;</button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#666' }}>Product: <strong>{editingProduct.name}</strong></p>

              {cityStockData.map((item, index) => (
                <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    placeholder="City (e.g. Jeddah)"
                    value={item.city}
                    onChange={(e) => handleStockChange(index, 'city', e.target.value)}
                    style={{ flex: 2, padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                  <input
                    type="number"
                    placeholder="Stock"
                    value={item.stock}
                    onChange={(e) => handleStockChange(index, 'stock', e.target.value)}
                    style={{ flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                  <button
                    onClick={() => handleRemoveCityRow(index)}
                    style={{ background: '#ffe6e6', color: '#dc3545', border: '1px solid #ffcccc', borderRadius: '4px', padding: '0 12px', cursor: 'pointer' }}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}

              <button
                onClick={handleAddCityRow}
                style={{ marginTop: '10px', padding: '8px 15px', background: '#e6f0ff', border: '1px dashed #004aad', color: '#004aad', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: '600' }}
              >
                <FaPlus /> Add City Row
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '15px 20px', borderTop: '1px solid #eee', background: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button
                onClick={() => setShowStockModal(false)}
                style={{ padding: '8px 16px', border: '1px solid #ccc', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', color: '#555' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveStock}
                disabled={savingStock}
                style={{ padding: '8px 20px', background: '#004aad', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px', opacity: savingStock ? 0.7 : 1 }}
              >
                {savingStock ? 'Saving...' : <><FaSave /> Save Stock</>}
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;